import {Upload} from 'tus-js-client';
import {UploadedFile} from '@ui/utils/files/uploaded-file';
import {UploadStrategy, UploadStrategyConfig} from './upload-strategy';
import {FileEntry} from '../../file-entry';
import {getAxiosErrorMessage} from '@common/http/get-axios-error-message';
import {apiClient} from '@common/http/query-client';
import {getCookie} from 'react-use-cookie';
import {EnhancedProgressTimeout, getTimeoutForFile} from '../enhanced-progress-timeout';

export class EnhancedTusUpload implements UploadStrategy {
  private upload: Upload;
  private progressTimer: EnhancedProgressTimeout;

  private constructor(upload: Upload, progressTimer: EnhancedProgressTimeout) {
    this.upload = upload;
    this.progressTimer = progressTimer;
  }

  start() {
    this.upload.start();
  }

  abort() {
    this.progressTimer.done();
    return this.upload.abort(true);
  }

  static async create(
    file: UploadedFile,
    {
      onProgress,
      onSuccess,
      onError,
      metadata,
      chunkSize,
      baseUrl,
    }: UploadStrategyConfig,
  ): Promise<EnhancedTusUpload> {
    const tusFingerprint = ['tus', file.fingerprint, 'drive'].join('-');
    
    // 파일 크기에 따른 최적화된 설정
    const optimizedConfig = getOptimizedConfig(file, chunkSize);
    
    // 향상된 타임아웃 설정
    const timeout = getTimeoutForFile(file.size);
    const progressTimer = new EnhancedProgressTimeout(timeout);
    
    const upload = new Upload(file.native, {
      fingerprint: () => Promise.resolve(tusFingerprint),
      removeFingerprintOnSuccess: true,
      endpoint: `${baseUrl}/api/v1/tus/upload`,
      chunkSize: optimizedConfig.chunkSize,
      retryDelays: optimizedConfig.retryDelays,
      overridePatchMethod: true,
      metadata: {
        name: window.btoa(file.id),
        clientName: file.name,
        clientExtension: file.extension,
        clientMime: file.mime || '',
        clientSize: `${file.size}`,
        ...(metadata as Record<string, string>),
      },
      headers: {
        'X-XSRF-TOKEN': getCookie('XSRF-TOKEN'),
      },
      onError: (err: any) => {
        progressTimer.done();
        if ('originalResponse' in err && err.originalResponse) {
          try {
            const message = JSON.parse(err.originalResponse.getBody())?.message;
            onError?.(message, file);
          } catch (e) {
            onError?.(null, file);
          }
        } else {
          onError?.(null, file);
        }
      },
      onProgress: (bytesUploaded: number, bytesTotal: number) => {
        progressTimer.progress();
        
        // 진행률 통계 로깅 (디버깅용)
        const stats = progressTimer.getProgressStats();
        if (stats.progressCount % 10 === 0) { // 10번마다 로깅
          console.log(`Upload progress: ${bytesUploaded}/${bytesTotal} bytes, Progress count: ${stats.progressCount}`);
        }
        
        onProgress?.({bytesUploaded, bytesTotal});
      },
      onSuccess: async () => {
        progressTimer.done();
        const uploadKey = upload.url?.split('/').pop();
        try {
          if (uploadKey) {
            const response = await createFileEntry(uploadKey);
            onSuccess?.(response.fileEntry, file);
          }
        } catch (err) {
          localStorage.removeItem(tusFingerprint);
          onError?.(getAxiosErrorMessage(err), file);
        }
      },
    });

    const previousUploads = await upload.findPreviousUploads();
    if (previousUploads.length) {
      upload.resumeFromPreviousUpload(previousUploads[0]);
    }

    return new EnhancedTusUpload(upload, progressTimer);
  }
}

// 파일 크기에 따른 최적화된 설정
function getOptimizedConfig(file: UploadedFile, defaultChunkSize?: number) {
  const sizeInMB = file.size / (1024 * 1024);
  const defaultSize = defaultChunkSize || 2 * 1024 * 1024; // 기본값 2MB
  
  let chunkSize = defaultSize;
  let retryDelays: number[];
  
  if (sizeInMB < 100) {
    // 작은 파일: 빠른 업로드
    chunkSize = Math.min(defaultSize, 2 * 1024 * 1024); // 최대 2MB
    retryDelays = [0, 2000, 5000, 10000, 20000];
  } else if (sizeInMB < 500) {
    // 중간 파일: 균형잡힌 설정
    chunkSize = Math.min(defaultSize, 5 * 1024 * 1024); // 최대 5MB
    retryDelays = [0, 3000, 8000, 15000, 30000];
  } else if (sizeInMB < 1000) {
    // 큰 파일: 안정성 우선
    chunkSize = Math.min(defaultSize, 10 * 1024 * 1024); // 최대 10MB
    retryDelays = [0, 5000, 10000, 20000, 40000];
  } else if (sizeInMB < 5000) {
    // 대용량 파일: 매우 안정적인 설정
    chunkSize = Math.min(defaultSize, 20 * 1024 * 1024); // 최대 20MB
    retryDelays = [0, 10000, 20000, 40000, 60000];
  } else {
    // 초대용량 파일: 최대 안정성
    chunkSize = Math.min(defaultSize, 50 * 1024 * 1024); // 최대 50MB
    retryDelays = [0, 15000, 30000, 60000, 120000];
  }
  
  return {
    chunkSize,
    retryDelays,
  };
}

function createFileEntry(uploadKey: string): Promise<{fileEntry: FileEntry}> {
  return apiClient.post('tus/entries', {uploadKey}).then(r => r.data);
} 